/**
 * OAuth callback route: /auth/callback.
 * Parses code/state, validates state, exchanges code for tokens, then redirects (no token in URL).
 * Uses a ref to run exchange only once and avoid redirect loops from double effect runs.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { exchangeCodeForTokens, clearAuth } from "./authService";
import { STORAGE_KEYS } from "./config";

function clearPkce() {
  try {
    window.sessionStorage.removeItem(STORAGE_KEYS.CODE_VERIFIER);
    window.sessionStorage.removeItem(STORAGE_KEYS.STATE);
  } catch {
    // ignore
  }
}

export default function AutodeskCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("Signing you in…");
  const exchangeStarted = useRef(false);

  useEffect(() => {
    // Parse code/state from raw query so '+' in the auth code is not turned into a space
    // (URLSearchParams uses application/x-www-form-urlencoded where + = space).
    const rawSearch = typeof window !== "undefined" ? window.location.search.slice(1) : "";
    const getParam = (name) => {
      const prefix = `${name}=`;
      for (const part of rawSearch.split("&")) {
        if (part.startsWith(prefix)) {
          const value = part.slice(prefix.length);
          return value ? decodeURIComponent(value.replace(/\+/g, " ")) : "";
        }
      }
      return null;
    };
    // Preserve '+' in code/state: decode %XX but do not treat + as space.
    const getParamStrict = (name) => {
      const prefix = `${name}=`;
      for (const part of rawSearch.split("&")) {
        if (part.startsWith(prefix)) {
          const value = part.slice(prefix.length);
          return value ? decodeURIComponent(value) : "";
        }
      }
      return null;
    };
    const code = getParamStrict("code");
    const stateFromUrl = getParamStrict("state");
    const errorParam = getParam("error");

    if (errorParam) {
      clearPkce();
      clearAuth();
      setStatus("Sign-in was denied or failed.");
      navigate("/login", { replace: true, state: { autodeskError: "denied" } });
      return;
    }

    if (!code || !stateFromUrl) {
      clearPkce();
      navigate("/login", { replace: true, state: { autodeskError: "missing_params" } });
      return;
    }

    if (exchangeStarted.current) return;
    exchangeStarted.current = true;

    let cancelled = false;
    exchangeCodeForTokens(code, stateFromUrl)
      .then(() => {
        if (cancelled) return;
        setStatus("Success. Redirecting…");
        navigate("/login", { replace: true });
      })
      .catch(() => {
        if (cancelled) return;
        clearPkce();
        clearAuth();
        setStatus("Sign-in failed. Redirecting…");
        navigate("/login", { replace: true, state: { autodeskError: "exchange_failed" } });
      });

    return () => { cancelled = true; };
  }, [searchParams, navigate]);

  return (
    <div className="flex items-center justify-center min-h-screen">
      <p className="text-lg">{status}</p>
    </div>
  );
}
